import { Injectable } from '@angular/core'
import { TableFilterService } from '@lla-platform/core/core-data-access'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { tap } from 'rxjs/operators'
import { ReceivableService } from '../receivable.service'
import {
  GetArInvoicesList,
  GetArInvoicesSummary,
  GetCategoryOverview,
  GetGpPackage,
  GetGpPackageList,
  GetGrossProfit,
  GetGrossProfitList,
  GetInvoice,
  GetInvoiceLinesList,
  GetInvoiceLinesOverview,
  GetInvoiceLinesPriceAdherence,
  GetInvoicePackageLinesList,
  GetInvoicesGeoLocations,
  GetInvoicesListOverview,
  GetPackageUnperformed
} from './ar-invoices.actions'
import { ArInvoicesStateModel } from './ar-invoices.model'
import { IInvoiceGrossProfitExtendedResponse } from '../../interfaces/invoice-gross-profit.interface'
import { IInvoiceGpPackageExtendedResponse } from '../../interfaces/invoice-gp-package.interface'

@State<ArInvoicesStateModel>({
  name: 'arInvoices'
})
@Injectable()
export class ArInvoicesState {
  constructor(
    private receivableService: ReceivableService,
    private tableFilterService: TableFilterService
  ) {}

  @Selector()
  static summaryResponse(state: ArInvoicesStateModel) {
    return state.summaryResponse
  }

  @Selector()
  static listResponse(state: ArInvoicesStateModel) {
    return state.listResponse
  }

  @Selector()
  static listOverviewResponse(state: ArInvoicesStateModel) {
    return state.listOverviewResponse
  }

  @Selector()
  static invoicesGeoLocations(state: ArInvoicesStateModel) {
    return state.invoicesGeoLocations ?? []
  }

  @Selector()
  static grossProfitListResponse(state: ArInvoicesStateModel) {
    return state.grossProfitListResponse
  }

  @Selector()
  static gpPackageListResponse(state: ArInvoicesStateModel) {
    return state.gpPackageListResponse
  }

  @Selector()
  static invoiceResponse(state: ArInvoicesStateModel) {
    return state.invoiceResponse
  }

  @Selector()
  static packageUnperformed(state: ArInvoicesStateModel) {
    return state.packageUnperformed
  }

  @Selector()
  static grossProfit(state: ArInvoicesStateModel): IInvoiceGrossProfitExtendedResponse | undefined {
    if (!state.grossProfit) {
      return
    }

    const maxC1Percent = Math.max(
      ...state.grossProfit.invoiceGrossProfits.map((el) => el.c1?.totalPercent ?? 0)
    )
    const maxC2Percent = Math.max(
      ...state.grossProfit.invoiceGrossProfits.map((el) => el.c2?.totalPercent ?? 0)
    )
    const maxC3Percent = Math.max(
      ...state.grossProfit.invoiceGrossProfits.map((el) => el.c3?.totalPercent ?? 0)
    )
    const maxC4Percent = Math.max(
      ...state.grossProfit.invoiceGrossProfits.map((el) => el.c4?.totalPercent ?? 0)
    )
    const maxC5Percent = Math.max(
      ...state.grossProfit.invoiceGrossProfits.map((el) => el.c5?.totalPercent ?? 0)
    )
    const maxTotalPercent = Math.max(
      ...state.grossProfit.invoiceGrossProfits.map((el) => el.total?.totalPercent ?? 0)
    )

    return {
      timePeriod: state.grossProfit.timePeriod,
      invoiceGrossProfits: state.grossProfit.invoiceGrossProfits.map((el) => ({
        locationId: el.locationId,
        locationName: el.locationName,
        c1Total: el.c1?.total ?? 0,
        c1Percent: el.c1?.totalPercent ?? 0,
        c1PercentGlobal: ((el.c1?.totalPercent ?? 0) / maxC1Percent) * 100,
        c1PercentFormatted: el.c1?.totalPercentFormatted,
        c2Total: el.c2?.total ?? 0,
        c2Percent: el.c2?.totalPercent ?? 0,
        c2PercentGlobal: ((el.c2?.totalPercent ?? 0) / maxC2Percent) * 100,
        c2PercentFormatted: el.c2?.totalPercentFormatted,
        c3Total: el.c3?.total ?? 0,
        c3Percent: el.c3?.totalPercent ?? 0,
        c3PercentGlobal: ((el.c3?.totalPercent ?? 0) / maxC3Percent) * 100,
        c3PercentFormatted: el.c3?.totalPercentFormatted,
        c4Total: el.c4?.total ?? 0,
        c4Percent: el.c4?.totalPercent ?? 0,
        c4PercentGlobal: ((el.c4?.totalPercent ?? 0) / maxC4Percent) * 100,
        c4PercentFormatted: el.c4?.totalPercentFormatted,
        c5Total: el.c5?.total ?? 0,
        c5Percent: el.c5?.totalPercent ?? 0,
        c5PercentGlobal: ((el.c5?.totalPercent ?? 0) / maxC5Percent) * 100,
        c5PercentFormatted: el.c5?.totalPercentFormatted,
        totalsTotal: el.total?.total ?? 0,
        totalsPercent: el.total?.totalPercent ?? 0,
        totalsPercentFormatted: el.total?.totalPercentFormatted,
        totalsPercentGlobal: ((el.total?.totalPercent ?? 0) / maxTotalPercent) * 100
      })),
      grandTotal: {
        c1Total: state.grossProfit.totalC1?.total ?? 0,
        c1Percent: state.grossProfit.totalC1?.totalPercent ?? 0,
        c1PercentFormatted: state.grossProfit.totalC1?.totalPercentFormatted,
        c2Total: state.grossProfit.totalC2?.total ?? 0,
        c2Percent: state.grossProfit.totalC2?.totalPercent ?? 0,
        c2PercentFormatted: state.grossProfit.totalC2?.totalPercentFormatted,
        c3Total: state.grossProfit.totalC3?.total ?? 0,
        c3Percent: state.grossProfit.totalC3?.totalPercent ?? 0,
        c3PercentFormatted: state.grossProfit.totalC3?.totalPercentFormatted,
        c4Total: state.grossProfit.totalC4?.total ?? 0,
        c4Percent: state.grossProfit.totalC4?.totalPercent ?? 0,
        c4PercentFormatted: state.grossProfit.totalC4?.totalPercentFormatted,
        c5Total: state.grossProfit.totalC5?.total ?? 0,
        c5Percent: state.grossProfit.totalC5?.totalPercent ?? 0,
        c5PercentFormatted: state.grossProfit.totalC5?.totalPercentFormatted,
        totalsTotal: state.grossProfit.grandTotals?.total ?? 0,
        totalsPercent: state.grossProfit.grandTotals?.totalPercent ?? 0,
        totalsPercentFormatted: state.grossProfit.grandTotals?.totalPercentFormatted
      }
    }
  }

  @Selector()
  static gpPackage(state: ArInvoicesStateModel): IInvoiceGpPackageExtendedResponse | undefined {
    if (!state.gpPackage) {
      return
    }

    return {
      timePeriod: state.gpPackage.timePeriod,
      items: state.gpPackage.items.map((el) => ({
        categoryId: el.categoryId,
        categoryName: el.categoryName,
        locationId: el.locationId,
        locationName: el.locationName,
        c1Total: el.c1?.total ?? 0,
        c1Percent: el.c1?.totalPercent ?? 0,
        c1PercentFormatted: el.c1?.totalPercentFormatted,
        c2Total: el.c2?.total ?? 0,
        c2Percent: el.c2?.totalPercent ?? 0,
        c2PercentFormatted: el.c2?.totalPercentFormatted,
        c3Total: el.c3?.total ?? 0,
        c3Percent: el.c3?.totalPercent ?? 0,
        c3PercentFormatted: el.c3?.totalPercentFormatted,
        c4Total: el.c4?.total ?? 0,
        c4Percent: el.c4?.totalPercent ?? 0,
        c4PercentFormatted: el.c4?.totalPercentFormatted,
        c5Total: el.c5?.total ?? 0,
        c5Percent: el.c5?.totalPercent ?? 0,
        c5PercentFormatted: el.c5?.totalPercentFormatted,
        totalsTotal: el.total?.total ?? 0,
        totalsPercent: el.total?.totalPercent ?? 0,
        totalsPercentFormatted: el.total?.totalPercentFormatted
      })),
      grandTotal: {
        c1Total: state.gpPackage.totalC1?.total ?? 0,
        c1Percent: state.gpPackage.totalC1?.totalPercent ?? 0,
        c1PercentFormatted: state.gpPackage.totalC1?.totalPercentFormatted,
        c2Total: state.gpPackage.totalC2?.total ?? 0,
        c2Percent: state.gpPackage.totalC2?.totalPercent ?? 0,
        c2PercentFormatted: state.gpPackage.totalC2?.totalPercentFormatted,
        c3Total: state.gpPackage.totalC3?.total ?? 0,
        c3Percent: state.gpPackage.totalC3?.totalPercent ?? 0,
        c3PercentFormatted: state.gpPackage.totalC3?.totalPercentFormatted,
        c4Total: state.gpPackage.totalC4?.total ?? 0,
        c4Percent: state.gpPackage.totalC4?.totalPercent ?? 0,
        c4PercentFormatted: state.gpPackage.totalC4?.totalPercentFormatted,
        c5Total: state.gpPackage.totalC5?.total ?? 0,
        c5Percent: state.gpPackage.totalC5?.totalPercent ?? 0,
        c5PercentFormatted: state.gpPackage.totalC5?.totalPercentFormatted,
        totalsTotal: state.gpPackage.grandTotals?.total ?? 0,
        totalsPercent: state.gpPackage.grandTotals?.totalPercent ?? 0,
        totalsPercentFormatted: state.gpPackage.grandTotals?.totalPercentFormatted
      }
    }
  }

  @Selector()
  static categoryOverview(state: ArInvoicesStateModel) {
    return state.categoryOverview
  }

  @Selector()
  static invoicePackageLinesResponse(state: ArInvoicesStateModel) {
    return state.invoicePackageLinesResponse
  }

  @Selector()
  static invoiceLinesOverviewResponse(state: ArInvoicesStateModel) {
    return state.invoiceLinesOverviewResponse
  }

  @Selector()
  static invoiceLinesPriceAdherenceResponse(state: ArInvoicesStateModel) {
    return state.invoiceLinesPriceAdherenceResponse
  }

  @Selector()
  static invoiceLinesListResponse(state: ArInvoicesStateModel) {
    return state.invoiceLinesListResponse
  }

  @Action(GetArInvoicesSummary)
  getCustomersTypes(ctx: StateContext<ArInvoicesStateModel>, { payload }: GetArInvoicesSummary) {
    ctx.patchState({
      summaryResponse: undefined
    })
    return this.receivableService.getArInvoicesSummary(payload).pipe(
      tap((res) => {
        ctx.patchState({
          summaryResponse: res
        })
      })
    )
  }

  @Action(GetInvoicesGeoLocations)
  getInvoicesGeoLocations(ctx: StateContext<ArInvoicesStateModel>, { info }: GetInvoicesGeoLocations) {
    ctx.patchState({
      invoicesGeoLocations: undefined
    })
    return this.receivableService.getInvoicesGeoLocations(info).pipe(
      tap((res) => {
        ctx.patchState({
          invoicesGeoLocations: res.items
        })
      })
    )
  }

  @Action(GetInvoicesListOverview)
  getInvoicesListOverview(ctx: StateContext<ArInvoicesStateModel>, { info }: GetInvoicesListOverview) {
    ctx.patchState({
      listOverviewResponse: undefined
    })
    return this.receivableService.getInvoicesListOverview(info).pipe(
      tap((res) => {
        ctx.patchState({
          listOverviewResponse: res
        })
      })
    )
  }

  @Action(GetArInvoicesList)
  getArInvoicesList(ctx: StateContext<ArInvoicesStateModel>, { info }: GetArInvoicesList) {
    ctx.patchState({
      listResponse: undefined
    })
    return this.receivableService.getArInvoicesList(info).pipe(
      tap((res) => {
        ctx.patchState({
          listResponse: res
        })
      })
    )
  }

  @Action(GetGrossProfitList)
  getGrossProfitList(ctx: StateContext<ArInvoicesStateModel>, { info }: GetGrossProfitList) {
    ctx.patchState({
      grossProfitListResponse: undefined
    })
    return this.receivableService.getGrossProfitList(info).pipe(
      tap((res) => {
        ctx.patchState({
          grossProfitListResponse: res
        })
      })
    )
  }

  @Action(GetGpPackageList)
  getGpPackageList(ctx: StateContext<ArInvoicesStateModel>, { info }: GetGpPackageList) {
    ctx.patchState({
      gpPackageListResponse: undefined
    })
    return this.receivableService.getGpPackageList(info).pipe(
      tap((res) => {
        ctx.patchState({
          gpPackageListResponse: res
        })
      })
    )
  }

  @Action(GetInvoice)
  getInvoice(ctx: StateContext<ArInvoicesStateModel>, { invoiceId }: GetInvoice) {
    ctx.patchState({
      invoiceResponse: undefined
    })
    return this.receivableService.getInvoice(invoiceId).pipe(
      tap((res) => {
        ctx.patchState({
          invoiceResponse: res
        })
      })
    )
  }

  @Action(GetGrossProfit)
  getGrossProfit(ctx: StateContext<ArInvoicesStateModel>, { payload }: GetGrossProfit) {
    ctx.patchState({
      grossProfit: undefined
    })
    return this.receivableService.getGrossProfit(payload).pipe(
      tap((res) => {
        ctx.patchState({
          grossProfit: res
        })
      })
    )
  }

  @Action(GetGpPackage)
  getGpPackage(ctx: StateContext<ArInvoicesStateModel>, { payload }: GetGpPackage) {
    ctx.patchState({
      gpPackage: undefined
    })
    return this.receivableService.getGpPackage(payload).pipe(
      tap((res) => {
        ctx.patchState({
          gpPackage: res
        })
      })
    )
  }

  @Action(GetCategoryOverview, { cancelUncompleted: true })
  getCategoryOverview(ctx: StateContext<ArInvoicesStateModel>, { payload }: GetCategoryOverview) {
    ctx.patchState({
      categoryOverview: undefined
    })
    return this.receivableService.getCategoryOverview(payload).pipe(
      tap((res) => {
        ctx.patchState({
          categoryOverview: res
        })
      })
    )
  }

  @Action(GetPackageUnperformed)
  getPackageUnperformed(ctx: StateContext<ArInvoicesStateModel>, { payload }: GetPackageUnperformed) {
    ctx.patchState({
      packageUnperformed: undefined
    })
    return this.receivableService.getPackageUnperformed(payload).pipe(
      tap((res) => {
        ctx.patchState({
          packageUnperformed: res
        })
      })
    )
  }

  @Action(GetInvoicePackageLinesList)
  getInvoicePackageLinesList(
    ctx: StateContext<ArInvoicesStateModel>,
    { info }: GetInvoicePackageLinesList
  ) {
    ctx.patchState({
      invoicePackageLinesResponse: undefined
    })
    return this.receivableService.getInvoicePackageLinesList(info).pipe(
      tap((res) => {
        ctx.patchState({
          invoicePackageLinesResponse: res
        })
      })
    )
  }

  @Action(GetInvoiceLinesOverview)
  getInvoiceLinesOverview(ctx: StateContext<ArInvoicesStateModel>, { info }: GetInvoiceLinesOverview) {
    ctx.patchState({
      invoiceLinesOverviewResponse: undefined
    })
    return this.receivableService.getInvoiceLinesOverview(info).pipe(
      tap((res) => {
        ctx.patchState({
          invoiceLinesOverviewResponse: res
        })
      })
    )
  }

  @Action(GetInvoiceLinesPriceAdherence)
  getInvoiceLinesPriceAdherence(
    ctx: StateContext<ArInvoicesStateModel>,
    { payload }: GetInvoiceLinesPriceAdherence
  ) {
    ctx.patchState({
      invoiceLinesPriceAdherenceResponse: undefined
    })
    return this.receivableService.getInvoiceLinesPriceAdherence(payload).pipe(
      tap((res) => {
        ctx.patchState({
          invoiceLinesPriceAdherenceResponse: res
        })
      })
    )
  }

  @Action(GetInvoiceLinesList)
  getInvoiceLinesList(ctx: StateContext<ArInvoicesStateModel>, { info }: GetInvoiceLinesList) {
    ctx.patchState({
      invoiceLinesListResponse: undefined
    })
    return this.receivableService.getInvoiceLinesList(info).pipe(
      tap((res) => {
        ctx.patchState({
          invoiceLinesListResponse: res
        })
      })
    )
  }
}
